import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../api";
import { toCsv, downloadCsv, sanitizeForFilename } from "../lib/csv";
import { formatMoney } from "../lib/format";
import { MONTHS_SHORT, MONTHS_LONG } from "../lib/months";
import { useSettings } from "../SettingsContext";
import SupplierOverview from "./SupplierOverview";
import TransactionsTable from "./TransactionsTable";

export const dueDateFor = (account, year, month) => {
  if (!account.payment_due_date) return null;
  const day = parseInt(account.payment_due_date.split("-")[2], 10);
  if (isNaN(day)) return null;
  const lastDay = new Date(year, month, 0).getDate();
  return new Date(year, month - 1, Math.min(day, lastDay));
};

const StatementsGrid = ({ account, onOpenStatement, title = "Statements", refreshKey = 0, extractingStatementIds = null, onExtract = null, onViewModeChange = null }) => {
  const { settings } = useSettings();
  const now = new Date();
  const yearStorageKey = `accountz.statementYear.${account.id}`;
  const [year, setYear] = useState(() => {
    const saved = parseInt(localStorage.getItem(yearStorageKey), 10);
    return Number.isFinite(saved) ? saved : now.getFullYear();
  });
  const [statements, setStatements] = useState([]);
  const [extractAllRunning, setExtractAllRunning] = useState(false);
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "table"
  const [showOverview, setShowOverview] = useState(false);

  // Persist the currently-viewed year so returning from a statement lands here
  useEffect(() => {
    try {
      localStorage.setItem(yearStorageKey, String(year));
    } catch {
      /* ignore quota errors */
    }
  }, [year, yearStorageKey]);

  const fetchStatements = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/accounts/${account.id}/statements?year=${year}`);
      setStatements(res.data);
    } catch (error) {
      console.error("Failed to fetch statements:", error);
    }
  }, [account.id, year, refreshKey]);

  useEffect(() => {
    fetchStatements();
  }, [fetchStatements]);

  const getOrCreate = async (month) => {
    const res = await axios.post(`${API}/accounts/${account.id}/statements`, { year, month });
    return res.data;
  };

  const openSlot = async (month) => {
    try {
      onOpenStatement(await getOrCreate(month));
    } catch (error) {
      console.error("Failed to open statement:", error);
      toast.error("Failed to open statement");
    }
  };

  const isPastOrPresent = (m) =>
    year < now.getFullYear() || (year === now.getFullYear() && m <= now.getMonth() + 1);

  const exportYearCsv = () => {
    // Determine transaction amount split columns based on account type
    const acctType = account.account_type;
    let amountHeaders;
    let splitAmount;
    if (acctType === "Credit Card") {
      amountHeaders = ["payment", "purchase"];
      splitAmount = (v) => {
        if (v === "" || v == null) return { payment: "", purchase: "" };
        const n = typeof v === "number" ? v : parseFloat(v);
        if (isNaN(n)) return { payment: "", purchase: "" };
        return n < 0
          ? { payment: Math.abs(n).toFixed(2), purchase: "" }
          : { payment: "", purchase: n.toFixed(2) };
      };
    } else if (acctType === "Bank Account") {
      amountHeaders = ["withdrawal", "deposit"];
      splitAmount = (v) => {
        if (v === "" || v == null) return { withdrawal: "", deposit: "" };
        const n = typeof v === "number" ? v : parseFloat(v);
        if (isNaN(n)) return { withdrawal: "", deposit: "" };
        return n < 0
          ? { withdrawal: Math.abs(n).toFixed(2), deposit: "" }
          : { withdrawal: "", deposit: n.toFixed(2) };
      };
    } else {
      amountHeaders = ["amount"];
      splitAmount = (v) => ({ amount: v ?? "" });
    }

    const rows = [];
    const sorted = [...statements].sort((a, b) => a.month - b.month);
    for (const s of sorted) {
      const monthName = MONTHS_LONG[s.month - 1] || "";
      const base = {
        year: s.year,
        month: monthName,
        statement_notes: s.notes || "",
      };
      const txs = s.transactions || [];
      if (txs.length === 0) {
        rows.push({
          ...base,
          transaction_date: "",
          transaction_description: "",
          ...splitAmount(null),
          transaction_note: "",
          tag: "",
          hst: "",
          tax: "",
          refund: "",
        });
      } else {
        for (const t of txs) {
          rows.push({
            ...base,
            transaction_date: t.date || "",
            transaction_description: t.description || "",
            ...splitAmount(t.amount),
            transaction_note: t.note || "",
            tag: t.tag || "",
            hst: t.hst ? "Yes" : "",
            tax: t.tax != null && t.tax !== "" ? Number(t.tax).toFixed(2) : "",
            refund: t.refund ? "Yes" : "",
          });
        }
      }
    }
    const headers = [
      "year",
      "month",
      "transaction_date",
      "transaction_description",
      ...amountHeaders,
      "transaction_note",
      "tag",
      "hst",
      "tax",
      "refund",
      "statement_notes",
    ];
    const csv = toCsv(rows, headers);
    const filename = `${sanitizeForFilename(account.name)}-statements-${year}.csv`;
    downloadCsv(filename, csv);
    toast.success(`Exported ${sorted.length} statement${sorted.length === 1 ? "" : "s"}`);
  };

  // Candidates for "Extract All": statements with attached documents but no transactions yet
  const extractAllCandidates = statements.filter(
    (s) => (s.document_count || 0) > 0 && (s.transactions?.length || 0) === 0
  );

  const totalTransactions = statements.reduce(
    (n, s) => n + (s.transactions?.length || 0),
    0
  );

  const extractAll = async () => {
    if (!onExtract || extractAllCandidates.length === 0) return;
    setExtractAllRunning(true);
    const total = extractAllCandidates.length;
    const toastId = `extract-all-${account.id}-${year}`;
    let completed = 0;
    let launched = 0;
    let failed = 0;
    toast.loading(`Extracting 0 of ${total}…`, { id: toastId });
    try {
      const tasks = extractAllCandidates.map(async (s) => {
        try {
          const filesRes = await axios.get(`${API}/statements/${s.id}/files`);
          const target = (filesRes.data || []).find((f) => !f.extracted);
          if (!target) return;
          launched += 1;
          const result = await onExtract(s, target, { silent: true });
          if (!result) failed += 1;
        } catch (err) {
          failed += 1;
          console.error(`Extract All failed for ${s.year}-${s.month}:`, err);
        } finally {
          completed += 1;
          toast.loading(`Extracting ${completed} of ${total}…`, { id: toastId });
        }
      });
      await Promise.all(tasks);
      if (launched === 0) {
        toast.info("No documents left to extract for this year.", { id: toastId });
      } else if (failed > 0) {
        toast.warning(
          `Extracted ${launched - failed} of ${launched} statement${launched === 1 ? "" : "s"} · ${failed} failed`,
          { id: toastId }
        );
      } else {
        toast.success(
          `Extracted ${launched} statement${launched === 1 ? "" : "s"}`,
          { id: toastId }
        );
      }
    } finally {
      setExtractAllRunning(false);
    }
  };

  return (
    <div data-testid="statements-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900">
          {title}
          {totalTransactions > 0 && (
            <button
              type="button"
              onClick={() => {
                const next = viewMode === "cards" ? "table" : "cards";
                setViewMode(next);
                if (onViewModeChange) onViewModeChange(next);
              }}
              className="ml-2 text-xs font-normal text-blue-600 hover:text-blue-800 hover:underline"
              data-testid="statements-year-tx-total"
              title={viewMode === "cards" ? "View all transactions in a table" : "Back to statement cards"}
            >
              · {totalTransactions.toLocaleString()} transaction{totalTransactions === 1 ? "" : "s"} · {viewMode === "cards" ? "view as table" : "view as cards"}
            </button>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {onExtract && extractAllCandidates.length > 0 && (
            <button
              onClick={extractAll}
              disabled={extractAllRunning}
              data-testid="statements-extract-all"
              title={`Extract transactions from ${extractAllCandidates.length} statement${extractAllCandidates.length === 1 ? "" : "s"} with attached documents`}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-800 border border-indigo-200 rounded-md px-2 py-1 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {extractAllRunning ? "Starting…" : `Extract All (${extractAllCandidates.length})`}
            </button>
          )}
          <button
            onClick={() => setShowOverview(true)}
            data-testid="statements-overview-button"
            title="Spending overview by tag & supplier"
            className="text-xs font-medium text-teal-600 hover:text-teal-800 border border-teal-200 rounded-md px-2 py-1 hover:bg-teal-50"
          >
            Overview
          </button>
          <button
            onClick={exportYearCsv}
            data-testid="statements-export-csv"
            disabled={statements.length === 0}
            className="text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Export CSV
          </button>
          <button
            onClick={() => setYear(year - 1)}
            data-testid="statements-prev-year"
            className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm"
          >
            ‹
          </button>
          <span className="text-sm font-semibold text-gray-900" data-testid="statements-year">{year}</span>
          <button
            onClick={() => setYear(year + 1)}
            data-testid="statements-next-year"
            className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm"
          >
            ›
          </button>
        </div>
      </div>

      {viewMode === "table" ? (
        <TransactionsTable
          statements={statements}
          accountType={account.account_type}
          onChanged={fetchStatements}
        />
      ) : (
      <div className="grid grid-cols-3 lg:grid-cols-4 gap-2" data-testid="statements-grid">
        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
          const stmt = statements.find((s) => s.month === m);
          const due = dueDateFor(account, year, m);
          const past = isPastOrPresent(m);
          const isCurrent = year === now.getFullYear() && m === now.getMonth() + 1;
          const amount = stmt?.amount ?? account.amount_due;
          const isExtracting = !!(stmt && extractingStatementIds?.has(stmt.id));
          return (
            <button
              key={m}
              onClick={() => openSlot(m)}
              data-testid={`statement-slot-${year}-${m}`}
              className={`relative text-left p-2.5 border rounded-lg bg-white hover:border-blue-400 transition-colors ${
                isCurrent ? "border-blue-500 ring-1 ring-blue-100" : "border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-xs font-bold uppercase tracking-wide ${past ? "text-gray-700" : "text-gray-400"}`}>
                  {MONTHS_SHORT[m - 1]}
                </span>
                <span className="flex items-center gap-1">
                  {isExtracting && (
                    <span
                      className="inline-flex items-center text-[10px] text-indigo-600 font-medium"
                      data-testid={`statement-extracting-${year}-${m}`}
                      title="Extracting transactions…"
                    >
                      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                    </span>
                  )}
                  {!isExtracting && stmt?.transactions?.length > 0 && (
                    <span
                      className="inline-flex items-center text-[10px] text-indigo-600 font-medium"
                      data-testid={`statement-tx-count-${year}-${m}`}
                      title={`${stmt.transactions.length} transaction${stmt.transactions.length === 1 ? "" : "s"}`}
                    >
                      <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                      </svg>
                      {stmt.transactions.length}
                    </span>
                  )}
                  {stmt?.document_count > 0 && (
                    <span
                      className="inline-flex items-center text-[10px] text-teal-600 font-medium"
                      data-testid={`statement-doc-count-${year}-${m}`}
                    >
                      <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      {stmt.document_count}
                    </span>
                  )}
                  {past && stmt?.is_paid && (
                    <span className="w-3.5 h-3.5 rounded-full bg-green-500 text-white text-[9px] flex items-center justify-center font-bold" data-testid={`statement-paid-${year}-${m}`}>
                      ✓
                    </span>
                  )}
                </span>
              </div>
              {past ? (
                <div className="mt-1 text-sm font-semibold text-gray-900">
                  {amount != null ? formatMoney(amount, settings) : "—"}
                </div>
              ) : (
                <div className="mt-1 text-xs text-gray-500">
                  {due ? `Due ${due.toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : "—"}
                </div>
              )}
            </button>
          );
        })}
      </div>
      )}

      {showOverview && (
        <SupplierOverview
          accountId={account.id}
          accountName={account.name}
          initialYear={year}
          onClose={() => setShowOverview(false)}
        />
      )}
    </div>
  );
};

export default StatementsGrid;

